import type { AuthorInfo, SearchArticleMeta } from "@serophin/nottldr-shared";
import { Coffee } from "lucide-preact";
import { AuthorCard } from "../author/AuthorCard";
import { Carousel } from "../explore/Carousel";
import { ArticleCard } from "./ArticleCard";
import { ArticleFeedback } from "./ArticleFeedback";
import { SignUpCard } from "./SignUpCard";

export type RelatedProps = {
  meta: SearchArticleMeta;
  author: AuthorInfo;
  authors: Record<string, AuthorInfo>;
  moreFromAuthor: SearchArticleMeta[];
  youMayAlsoLike: SearchArticleMeta[];
};

const DISCLAIMER =
  "This article and its author are AI-generated, and it was published without human review. It is for general informational purposes only and should not be considered as professional advice. Verify information independently and consult with qualified professionals before making any decisions based on this content.";

const Related = ({ title, testId, metas, authors }: { title: string; testId: string; metas: SearchArticleMeta[]; authors: Record<string, AuthorInfo> }) =>
  metas.length > 0 && (
    <section class="mt-8" data-testid={testId}>
      <h3 class="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
      <Carousel>
        {metas.map((meta, index) => (
          <ArticleCard key={meta.id} meta={meta} author={authors[meta.authorAlias]} class="mb-3" priority={index === 0} />
        ))}
      </Carousel>
    </section>
  );

const TipLink = () => (
  <a
    href="/tip"
    class="block text-center text-lg mt-8 bg-white dark:bg-neutral-700 rounded-lg border border-neutral-200 dark:border-neutral-700 p-3 text-neutral-900 dark:text-neutral-50 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-600/50 transition-colors"
  >
    <span class="block mb-3">Enjoyed this article?</span>
    <span class="font-bold flex items-center justify-center">
      <Coffee class="w-6 h-6 inline-block mr-1" />
      Tip me!
    </span>
  </a>
);

/** What sits below the article: disclaimer, feedback, tip, sign-up, related articles and the author. */
export const ArticleAside = ({ meta, author, authors, moreFromAuthor, youMayAlsoLike }: RelatedProps) => (
  <aside data-reader-ignore="true">
    <div class="disclaimer-box mt-8 p-3 bg-neutral-100 dark:bg-neutral-700 rounded-lg border border-neutral-200 dark:border-neutral-700">
      <p class="text-xs italic text-neutral-600 dark:text-neutral-300">{DISCLAIMER}</p>
    </div>
    <section class="mt-8" data-testid="feedback-section">
      <ArticleFeedback articleId={meta.id} authorAlias={meta.authorAlias} />
    </section>
    <TipLink />
    <SignUpCard testId="sign-up-article" class="visible-anonymous my-6" />
    <Related title="You may also like" testId="you-may-also-like-section" metas={youMayAlsoLike} authors={authors} />
    <Related title={`More from ${author.alias}`} testId="more-from-author-section" metas={moreFromAuthor} authors={authors} />
    <section class="mt-8" data-testid="author-details-section">
      <h3 class="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Author</h3>
      <AuthorCard author={author} />
    </section>
  </aside>
);
